import {ApolloError} from '@apollo/client'
import React, {useMemo} from 'react'
import {generatePath} from 'react-router'
import {Link as RouterLink} from 'react-router-dom'
import {Routes} from '../../common/Routes'
import {ActivityArea} from '../../common/types/activityArea'
import {Box} from '../../webui/Box'
import {Link} from '../../webui/Link'
import {Paper} from '../../webui/Paper'
import {LoadingSpinnerContainer} from '../../webui/progress/LoadingSpinnerContainer'
import {SortablePaginatedTable} from '../../webui/table/sortable-paginated-table/SortablePaginatedTable'
import {TableHeadCell} from '../../webui/table/sortable-paginated-table/TableHeadCell'
import {ActivityAreaData} from './types/ActivityAreaData'

// Stable renderers
const NameCell: TableHeadCell<ActivityAreaData>['render'] = (row) => (
  <Link component={RouterLink} to={generatePath(Routes.ACTIVITY_AREA, {id: row.id})}>
    {row.location?.name ?? '(no name)'}
  </Link>
)

const LocalityCell: TableHeadCell<ActivityAreaData>['render'] = (row) => row.location?.locality ?? ''

const ActivitiesCell: TableHeadCell<ActivityAreaData>['render'] = (row) => (row.activities?.length ? row.activities.join(', ') : '—')

const TypeCell: TableHeadCell<ActivityAreaData>['render'] = (row) => row.type ?? ''

const VerifiedCell: TableHeadCell<ActivityAreaData>['render'] = (row) => (row.verified ? 'Yes' : 'No')

const ActiveCell: TableHeadCell<ActivityAreaData>['render'] = (row) => (row.active ? 'Active' : 'Inactive')

// NOTE: `TableHeadCell` does not support `sortValue`, so we only use allowed props.
// Also, `id` must be keyof ActivityAreaData, so we use existing keys.
const createTableHeadCells: TableHeadCell<ActivityAreaData>[] = [
  {
    id: 'id', // sorting key; renderer shows location.name
    label: 'Name',
    maxWidth: 240,
    render: NameCell,
  },
  {
    id: 'location',
    label: 'City/Locality',
    maxWidth: 120,
    render: LocalityCell,
  },
  {
    id: 'activities',
    label: 'Activities',
    render: ActivitiesCell,
  },
  {
    id: 'type',
    label: 'Type',
    render: TypeCell,
  },
  {
    id: 'verified',
    label: 'Verified',
    render: VerifiedCell,
  },
  {
    id: 'active',
    label: 'Status',
    render: ActiveCell,
  },
]

const toRow = (a: ActivityArea): ActivityAreaData => ({
  id: a.id,
  active: a.active,
  activities: a.activities ?? [],
  location: a.location,
  type: a.type,
  verified: a.verified,
})

interface Props {
  activityAreas: ActivityArea[]
  error?: ApolloError
  loading?: boolean
}

export const ActivityAreasTable: React.FC<Props> = ({activityAreas, error, loading}) => {
  const rows = useMemo(() => (activityAreas ?? []).map(toRow), [activityAreas])

  if (error) return <div>{error.message}</div>
  if (loading) return <LoadingSpinnerContainer />

  if (!rows.length) {
    return (
      <Box my={2}>
        <Paper>
          <Box p={2}>No activity areas found.</Box>
        </Paper>
      </Box>
    )
  }

  return (
    <Box my={2}>
      <Paper>
        <SortablePaginatedTable<ActivityAreaData>
          title="Activity Areas"
          initialRowsPerPage={20}
          initialOrderByKey="id"
          tableHeadCells={createTableHeadCells}
          rows={rows}
          rowLinkTo={(row) => generatePath(Routes.ACTIVITY_AREA, {id: row.id})}
        />
      </Paper>
    </Box>
  )
}
